
import { getCurrentConference } from '../base/conference';
import { setActiveModalId } from '../base/modal';
import { PARTICIPANT_KICKED, PARTICIPANT_LEFT } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';

import { TOGGLE_DOCUMENT_EDITING } from './actionTypes';
import { setDocumentEditingState, setDocumentUrl, whichParticipant } from './actions';
import { SHARE_DOCUMENT_VIEW_ID } from './constants';

const ETHERPAD_COMMAND = 'etherpad';

/**
 * Middleware that captures actions related to collaborative document editing
 * and notifies components not hooked into redux.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */

MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    switch (action.type) {
    case TOGGLE_DOCUMENT_EDITING: {
        const state = getState();
        const editing = !state['features/etherpad'].editing;

        dispatch(setDocumentEditingState(editing));

        // For native apps, also handle modal visibility
        if (typeof APP === 'undefined') {
            if (editing) {
                dispatch(setActiveModalId(SHARE_DOCUMENT_VIEW_ID));
            } else if (state['features/base/modal'].activeModalId === SHARE_DOCUMENT_VIEW_ID) {
                dispatch(setActiveModalId(undefined));
            }
        }
        break;
    }

    case PARTICIPANT_LEFT: {
        const state = getState();
        const etherpadState = state['features/etherpad'];

        // Early exit: only process if etherpad is currently being edited
        if (!etherpadState?.editing) {
            break;
        }

        const leftParticipantId = action.participant?.id;

        // If the participant who was sharing the etherpad left, reset the state
        if (leftParticipantId && etherpadState?.partId === leftParticipantId) {
            dispatch(setDocumentEditingState(false));
            dispatch(whichParticipant(null));

            // dispatch(setDocumentUrl(undefined));

            // Close modal if open
            if (typeof APP === 'undefined' && state['features/base/modal']?.activeModalId === SHARE_DOCUMENT_VIEW_ID) {
                dispatch(setActiveModalId(undefined));
            }
        }
        break;
    }

    case PARTICIPANT_KICKED: {
        const state = getState();
        const etherpadState = state['features/etherpad'];

        // Early exit: only process if etherpad is currently being edited
        if (!etherpadState?.editing) {
            break;
        }

        const kickedParticipantId = action.kicked;

        // If the participant who was sharing the etherpad was kicked, reset the state
        if (kickedParticipantId && etherpadState?.partId === kickedParticipantId) {
            dispatch(setDocumentEditingState(false));
            dispatch(whichParticipant(null));

            // dispatch(setDocumentUrl(undefined));

            // Close modal if open
            if (typeof APP === 'undefined' && state['features/base/modal']?.activeModalId === SHARE_DOCUMENT_VIEW_ID) {
                dispatch(setActiveModalId(undefined));
            }
        }
        break;
    }
    }

    return next(action);
});

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear messages or close the chat modal if it's left
 * open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, { dispatch, getState }, previousConference) => {
        if (conference) {
            conference.addCommandListener(ETHERPAD_COMMAND,
                ({ value }) => {
                    let url;

                    if (navigator.product !== 'ReactNative') {
                        const { etherpad_base: etherpadBase } = getState()['features/base/config'];

                        if (etherpadBase) {
                            const u = new URL(value, etherpadBase);

                            url = u.toString();
                        }

                        dispatch(setDocumentUrl(url));
                    }
                }
            );
        }

        if (previousConference) {
            dispatch(setDocumentUrl(undefined));
        }
    });
